import React from 'react';
import { formatLiveTenure } from './utils/tenure';
import { NewFromRefreshBadge } from './components/NewFromRefreshBadge';

interface CardViewProps {
  data: any[];
  dataType: 'candidates' | 'companies' | 'managers';
  selectedRows: Set<number>;
  onToggleSelect: (id: number) => void;
  onSelectAll: () => void;
  onRowClick: (row: any) => void;
  onRowHover?: (row: any) => void;
  onCompanyAssociationClick?: (companyName: string, tab: 'candidates' | 'managers') => void;
  companyCandidateCounts?: Map<number, { total: number; filtered: number }>;
  hasActiveFilters?: boolean;
  totalCount: number;
}

const getInitials = (name: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const CandidateCard: React.FC<{
  row: any;
  selected: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
}> = ({ row, selected, onToggleSelect, onClick }) => {
  const hasEmail = row.email || row.personal_email;
  const hasPhone = row.phone;

  return (
    <div
      data-tour="record-card" className={`card card-hover p-4 cursor-pointer ${selected ? 'ring-2 ring-blue-500 border-blue-300' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="pt-0.5">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
            className="cursor-pointer rounded"
          />
        </div>

        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
          {getInitials(row.name)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-blue-700 truncate">{row.name || 'Unknown'}</h3>
                <NewFromRefreshBadge row={row} />
                {row.li_profile_url && (
                  <a
                    href={row.li_profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#0A66C2] hover:text-[#004182] transition-colors flex-shrink-0"
                    title="View LinkedIn profile"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                )}
              </div>
              <p className="text-sm text-gray-900 truncate">
                {row.current_position || row.headline || ''}
                {row.current_company && <span className="text-gray-500"> · {row.current_company}</span>}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {row.location_full || row.address || ''}
                {formatLiveTenure(row) && (
                  <span> · {formatLiveTenure(row)} in role</span>
                )}
                {row.total_experience_years > 0 && (
                  <span> · {row.total_experience_years}yr total exp</span>
                )}
              </p>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {hasEmail && (
                <span className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center" title={row.email || row.personal_email}>
                  <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </span>
              )}
              {hasPhone && (
                <span className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center" title={row.phone}>
                  <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
              )}
            </div>
          </div>

          {/* Summary snippet */}
          {row.summary && (
            <p className="text-xs text-gray-600 mt-2 line-clamp-2">
              {row.summary}
            </p>
          )}

          {/* Tags + match score */}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {row.tags && row.tags.length > 0 && row.tags.slice(0, 4).map((tag: string, i: number) => (
              <span key={i} className="badge badge-blue">{tag}</span>
            ))}
            {row.tags && row.tags.length > 4 && (
              <span className="text-[10px] text-gray-400">+{row.tags.length - 4}</span>
            )}
            {row.match_score != null && (
              <span className={`badge ${
                row.match_score >= 80 ? 'badge-green' :
                row.match_score >= 60 ? 'badge-blue' :
                row.match_score >= 40 ? 'badge-yellow' :
                'badge-gray'
              }`}>
                Score: {row.match_score}
              </span>
            )}
            {row.current_contract_type && (
              <span className="badge badge-gray">{row.current_contract_type}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CompanyCard: React.FC<{
  row: any;
  selected: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
  onAssociationClick?: (companyName: string, tab: 'candidates' | 'managers') => void;
  candidateCounts?: { total: number; filtered: number };
  hasActiveFilters?: boolean;
}> = ({ row, selected, onToggleSelect, onClick, onAssociationClick, candidateCounts, hasActiveFilters }) => (
  <div
    data-tour="record-card" className={`card card-hover p-4 cursor-pointer ${selected ? 'ring-2 ring-blue-500 border-blue-300' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer rounded"
        />
      </div>

      {row.logo_url ? (
        <img
          src={row.logo_url}
          alt={row.company_name}
          className="w-12 h-12 rounded-xl object-contain bg-white border border-gray-100 flex-shrink-0"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }}
        />
      ) : null}
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${row.logo_url ? 'hidden' : ''}`}>
        {getInitials(row.company_name)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{row.company_name || 'Unknown'}</h3>
              {row.url && (
                <a
                  href={row.url.startsWith('http') ? row.url : `https://${row.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[#0A66C2] hover:text-[#004182] transition-colors flex-shrink-0"
                  title="View LinkedIn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              )}
            </div>
            <p className="text-sm text-gray-600 truncate">
              {row.industry || ''}
              {row.subindustry && row.subindustry.length > 0 && (
                <span className="text-gray-400"> · {Array.isArray(row.subindustry) ? row.subindustry.join(', ') : row.subindustry}</span>
              )}
              {row.company_size && <span> · {row.company_size.toLocaleString()} employees</span>}
            </p>
            {row.address && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{row.address}</p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {(() => {
              const counts = candidateCounts;
              const total = counts?.total || row.candidate_count || 0;
              const filtered = counts?.filtered;
              const isFiltered = hasActiveFilters && filtered !== undefined && filtered < total;
              if (total <= 0) return null;
              return (
                <button
                  onClick={(e) => { e.stopPropagation(); onAssociationClick?.(row.company_name, 'candidates'); }}
                  className={`badge cursor-pointer hover:ring-2 transition-all ${isFiltered ? 'badge-yellow hover:ring-amber-300' : 'badge-blue hover:ring-blue-300'}`}
                  title={`View candidates at ${row.company_name}`}
                >
                  {isFiltered ? `${filtered}/${total}` : total} candidates
                </button>
              );
            })()}
            {row.hiring_manager_count > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onAssociationClick?.(row.company_name, 'managers'); }}
                className="badge badge-purple cursor-pointer hover:ring-2 hover:ring-purple-300 transition-all"
                title={`View hiring managers at ${row.company_name}`}
              >
                {row.hiring_manager_count} HMs
              </button>
            )}
          </div>
        </div>

        {row.specialties && (
          <p className="text-xs text-gray-600 mt-2 line-clamp-2">
            {Array.isArray(row.specialties) ? row.specialties.join(', ') :
             typeof row.specialties === 'string' && row.specialties.startsWith('[') ?
               JSON.parse(row.specialties).join(', ') : row.specialties}
          </p>
        )}

        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {row.website && (
            <a
              href={row.website.startsWith('http') ? row.website : `https://${row.website}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-blue-600 hover:text-blue-700 transition-colors truncate max-w-[200px]"
            >
              {row.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          )}
          {row.tags && row.tags.length > 0 && row.tags.slice(0, 3).map((tag: string, i: number) => (
            <span key={i} className="badge badge-purple">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ManagerCard: React.FC<{
  row: any;
  selected: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
}> = ({ row, selected, onToggleSelect, onClick }) => (
  <div
    data-tour="record-card" className={`card card-hover p-4 cursor-pointer ${selected ? 'ring-2 ring-blue-500 border-blue-300' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer rounded"
        />
      </div>

      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
        {getInitials(row.name)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{row.name || 'Unknown'}</h3>
              {row.linkedin_url && (
                <a
                  href={row.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[#0A66C2] hover:text-[#004182] transition-colors flex-shrink-0"
                  title="View LinkedIn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              )}
            </div>
            <p className="text-sm text-gray-600 truncate">
              {row.job_title || ''}
              {row.org && <span> · {row.org}</span>}
            </p>
            {row.location && (
              <p className="text-xs text-gray-500 mt-0.5">{row.location}</p>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {row.email && (
              <span className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center" title={row.email}>
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </span>
            )}
            {row.phone && (
              <span className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center" title={row.phone}>
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </span>
            )}
          </div>
        </div>

        {row.headline && (
          <p className="text-xs text-gray-600 mt-2 line-clamp-2">{row.headline}</p>
        )}

        {row.tags && row.tags.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {row.tags.slice(0, 4).map((tag: string, i: number) => (
              <span key={i} className="badge badge-yellow">{tag}</span>
            ))}
            {row.tags.length > 4 && (
              <span className="text-[10px] text-gray-400">+{row.tags.length - 4}</span>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

const CardView: React.FC<CardViewProps> = ({
  data,
  dataType,
  selectedRows,
  onToggleSelect,
  onSelectAll,
  onRowClick,
  onRowHover,
  onCompanyAssociationClick,
  companyCandidateCounts,
  hasActiveFilters,
  totalCount
}) => {
  if (data.length === 0) return null;

  return (
    <div>
      {/* Select all bar */}
      <div className="flex items-center gap-3 px-1 py-2 mb-3">
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
          <input
            type="checkbox"
            checked={selectedRows.size === data.length && data.length > 0}
            onChange={onSelectAll}
            className="cursor-pointer rounded"
          />
          Select all ({data.length})
        </label>
        {selectedRows.size > 0 && (
          <span className="text-xs text-blue-600 font-medium">{selectedRows.size} selected</span>
        )}
      </div>

      {/* Cards grid */}
      <div className="space-y-2">
        {data.map((row) => {
          const isSelected = selectedRows.has(row.id);
          const toggleSelect = () => onToggleSelect(row.id);
          const handleClick = () => onRowClick(row);
          const handleHover = () => { if (onRowHover) onRowHover(row); };

          if (dataType === 'candidates') {
            return <div key={row.id} onMouseEnter={handleHover}><CandidateCard row={row} selected={isSelected} onToggleSelect={toggleSelect} onClick={handleClick} /></div>;
          } else if (dataType === 'companies') {
            return <CompanyCard key={row.id} row={row} selected={isSelected} onToggleSelect={toggleSelect} onClick={handleClick} onAssociationClick={onCompanyAssociationClick} candidateCounts={companyCandidateCounts?.get(row.id)} hasActiveFilters={hasActiveFilters} />;
          } else {
            return <div key={row.id} onMouseEnter={handleHover}><ManagerCard row={row} selected={isSelected} onToggleSelect={toggleSelect} onClick={handleClick} /></div>;
          }
        })}
      </div>
    </div>
  );
};

export default CardView;
